import {
    DECOMPOSITION_MODE_EXECUTION,
    DECOMPOSITION_MODE_REALIZATION,
    DECOMPOSITION_MODE_RISK,
} from './decompositionModes';

const SPECIFIC_COMPONENT_BY_MODE = {
    [DECOMPOSITION_MODE_RISK]: 'idio',
    [DECOMPOSITION_MODE_EXECUTION]: 'non_trading',
    [DECOMPOSITION_MODE_REALIZATION]: 'unrealized',
};

function toFiniteNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function resolveAtOrBefore(dates, target, fallback) {
    const normalizedTarget = String(target || '').trim();
    if (!normalizedTarget) return fallback;
    if (dates.includes(normalizedTarget)) return normalizedTarget;

    let resolved = null;
    for (const dateValue of dates) {
        if (dateValue <= normalizedTarget) {
            resolved = dateValue;
            continue;
        }
        break;
    }
    return resolved || fallback;
}

function resolveWindowDates({
    dates,
    selectedTimestamp,
    selectedRangeStart,
    selectedRangeEnd,
    seriesMode,
}) {
    if (!Array.isArray(dates) || !dates.length) return [];

    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    const rangeStartRaw = String(selectedRangeStart || '').trim();
    const rangeEndRaw = String(selectedRangeEnd || '').trim();
    const hasExplicitRange = Boolean(
        rangeStartRaw
        && rangeEndRaw
        && rangeStartRaw !== rangeEndRaw
    );

    if (hasExplicitRange) {
        let rangeStart = resolveAtOrBefore(dates, rangeStartRaw, firstDate);
        let rangeEnd = resolveAtOrBefore(dates, rangeEndRaw, lastDate);
        if (!rangeStart || !rangeEnd) return [];
        if (rangeStart > rangeEnd) {
            const tmp = rangeStart;
            rangeStart = rangeEnd;
            rangeEnd = tmp;
        }
        const startIdx = dates.indexOf(rangeStart);
        const endIdx = dates.indexOf(rangeEnd);
        if (startIdx < 0 || endIdx < 0) return [];
        return dates.slice(startIdx, endIdx + 1);
    }

    const resolvedPoint = resolveAtOrBefore(
        dates,
        selectedTimestamp,
        lastDate
    );
    if (!resolvedPoint) return [];
    if (seriesMode === 'absolute') return [resolvedPoint];
    const endIdx = dates.indexOf(resolvedPoint);
    if (endIdx < 0) return [];
    return dates.slice(0, endIdx + 1);
}

function computeSharpeFromSeries(values) {
    if (!values.length) return null;
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const variance = values.reduce((sum, value) => {
        const diff = value - mean;
        return sum + (diff * diff);
    }, 0) / values.length;
    const stdDev = Math.sqrt(Math.max(variance, 0));
    if (!Number.isFinite(stdDev) || stdDev === 0) return 0;
    return mean / stdDev;
}

export function computeWindowSummaryMetrics({
    returns,
    mode,
    selectedTimestamp = null,
    selectedRangeStart = null,
    selectedRangeEnd = null,
    seriesMode = 'cumulative',
}) {
    const dates = Array.isArray(returns?.dates) ? returns.dates : [];
    if (!dates.length) {
        return {
            total_sharpe: null,
            specific_sharpe: null,
            hit_rate: null,
        };
    }

    const windowDates = resolveWindowDates({
        dates,
        selectedTimestamp,
        selectedRangeStart,
        selectedRangeEnd,
        seriesMode,
    });
    if (!windowDates.length) {
        return {
            total_sharpe: null,
            specific_sharpe: null,
            hit_rate: null,
        };
    }

    const specificKey = SPECIFIC_COMPONENT_BY_MODE[mode] || 'idio';
    const totalAbsolutePctSeries = returns?.total?.absolute_pct || {};
    const specificAbsolutePctSeries = returns?.[specificKey]?.absolute_pct || {};

    const totalValues = windowDates.map((date) => toFiniteNumber(totalAbsolutePctSeries?.[date], 0));
    const specificValues = windowDates.map((date) => toFiniteNumber(specificAbsolutePctSeries?.[date], 0));

    const hitRate = totalValues.length
        ? totalValues.filter((value) => value > 0).length / totalValues.length
        : null;

    return {
        total_sharpe: computeSharpeFromSeries(totalValues),
        specific_sharpe: computeSharpeFromSeries(specificValues),
        hit_rate: hitRate,
    };
}
